// Состояние одного диалога: сообщения, поток, инструменты, отмена, возврат
// к прерванной генерации.

import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type UploadedFile } from './api'
import type { ServerEvent } from './protocol'
import { createTransport, type Transport, type TransportKind } from './transport'

export interface ToolRun {
  id: number
  display: string
  summary: string
  done: boolean
  ok?: boolean
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  text: string
  /** Инструменты, отработавшие в рамках этого ответа. */
  tools?: ToolRun[]
  /** Файлы, которые модель отдала пользователю. */
  files?: { path: string; name: string }[]
  /** Записи в память, сделанные по ходу ответа. */
  memories?: string[]
  /** Ответ ещё генерируется. */
  streaming?: boolean
  /** Часть потока потеряна при переподключении — ждём done. */
  truncated?: boolean
  error?: boolean
}

/** Пауза перед попыткой вернуться к генерации, по номеру попытки. */
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000]

let idCounter = 0
const nextId = () => `m${++idCounter}`

export function useChat(
  convId: number | null,
  onConvCreated: (id: number) => void,
  /**
   * Called when there is no conversation to send into yet, and must return
   * the id of a fresh one.
   *
   * Sending `conv_id: null` is not an option: the server reads that as "use
   * whatever this chat was last in", so a message typed before the app had
   * settled would silently land in an old conversation.
   */
  ensureConversation?: () => Promise<number | null>,
) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [busy, setBusy] = useState(false)
  const [reconnecting, setReconnecting] = useState(false)
  const [transportKind, setTransportKind] = useState<TransportKind | null>(null)

  const transport = useRef<Transport | null>(null)
  /** Диалог, в котором сейчас идёт ответ: нужен для resume. */
  const streamingConv = useRef<number | null>(null)
  const busyRef = useRef(false)
  const retryAttempt = useRef(0)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setBusyBoth = useCallback((v: boolean) => {
    busyRef.current = v
    setBusy(v)
  }, [])

  /** Правит последнее сообщение ассистента (то, что сейчас пишется). */
  const patchLast = useCallback((fn: (m: ChatMessage) => ChatMessage) => {
    setMessages((prev) => {
      if (prev.length === 0) return prev
      const copy = [...prev]
      copy[copy.length - 1] = fn(copy[copy.length - 1])
      return copy
    })
  }, [])

  /** Догружает историю с сервера — последнее слово всегда за БД. */
  const reloadHistory = useCallback(async (id: number) => {
    try {
      const rows = await api.messages(id)
      setMessages(
        rows.map((r) => ({ id: nextId(), role: r.role, text: r.content })),
      )
    } catch {
      // не вышло — оставляем что есть
    }
  }, [])

  const handleEvent = useCallback(
    (ev: ServerEvent) => {
      switch (ev.type) {
        case 'ready':
          streamingConv.current = ev.conv_id
          onConvCreated(ev.conv_id)
          break

        case 'token':
          patchLast((m) => ({ ...m, text: m.text + ev.delta }))
          break

        case 'truncated':
          // Сервер не сохранил начало ответа в буфере повтора: показанный
          // текст неполный, но done приедет целиком.
          patchLast((m) => ({ ...m, truncated: true }))
          break

        case 'tool':
          patchLast((m) => {
            const tools = [...(m.tools ?? [])]
            const at = tools.findIndex((t) => t.id === ev.id)
            const run: ToolRun = {
              id: ev.id,
              display: ev.display,
              summary: ev.summary,
              done: ev.state === 'done',
              ok: ev.ok,
            }
            if (at === -1) tools.push(run)
            else tools[at] = run
            return { ...m, tools }
          })
          break

        case 'memory':
          patchLast((m) => ({
            ...m,
            memories: [...(m.memories ?? []), ev.text],
          }))
          break

        case 'file':
          patchLast((m) => ({
            ...m,
            files: [...(m.files ?? []), { path: ev.path, name: ev.name }],
          }))
          break

        case 'done':
          // Авторитетен именно done.text: он лёг в БД, а поток мог разойтись
          // (преамбула перед вызовом тула, откат на запасную модель) или
          // приехать урезанным после переподключения.
          patchLast((m) => ({
            ...m,
            text: ev.text,
            streaming: false,
            truncated: false,
          }))
          setBusyBoth(false)
          setReconnecting(false)
          retryAttempt.current = 0
          streamingConv.current = null
          break

        case 'idle':
          // Возвращаться не к чему: ответ либо дописался, пока связи не было,
          // либо генерации не было вовсе. Правду берём из БД.
          setBusyBoth(false)
          setReconnecting(false)
          retryAttempt.current = 0
          streamingConv.current = null
          void reloadHistory(ev.conv_id)
          break

        case 'cancelled':
          patchLast((m) => ({
            ...m,
            streaming: false,
            text: m.text || 'остановлено',
          }))
          setBusyBoth(false)
          setReconnecting(false)
          streamingConv.current = null
          break

        case 'error':
          patchLast((m) => ({
            ...m,
            streaming: false,
            error: true,
            text: m.text || ev.message,
          }))
          setBusyBoth(false)
          setReconnecting(false)
          streamingConv.current = null
          break
      }
    },
    [onConvCreated, patchLast, reloadHistory, setBusyBoth],
  )

  /** Пытается вернуться к прерванной генерации, с нарастающей паузой. */
  const scheduleResume = useCallback(() => {
    const conv = streamingConv.current
    if (!busyRef.current || conv === null) return

    const attempt = retryAttempt.current
    if (attempt >= RETRY_DELAYS_MS.length) {
      // Больше не пробуем: показываем правду из БД.
      setReconnecting(false)
      setBusyBoth(false)
      patchLast((m) => ({ ...m, streaming: false }))
      void reloadHistory(conv)
      return
    }

    retryAttempt.current = attempt + 1
    setReconnecting(true)
    retryTimer.current = setTimeout(() => {
      transport.current?.resume(conv, handleEvent).catch(() => scheduleResume())
    }, RETRY_DELAYS_MS[attempt])
  }, [handleEvent, patchLast, reloadHistory, setBusyBoth])

  // Транспорт создаётся один раз: ?transport=sse принудительно включает запасной.
  useEffect(() => {
    let cancelled = false
    const forced = new URLSearchParams(location.search).get('transport')
    createTransport(
      forced === 'sse' || forced === 'ws' ? (forced as TransportKind) : undefined,
    ).then((t) => {
      if (cancelled) {
        t.close()
        return
      }
      transport.current = t
      t.setOnDisconnect(() => scheduleResume())
      setTransportKind(t.kind)
    })
    return () => {
      cancelled = true
      if (retryTimer.current) clearTimeout(retryTimer.current)
      transport.current?.setOnDisconnect(null)
      transport.current?.close()
      transport.current = null
    }
  }, [scheduleResume])

  // Телефон усыпляет вкладку и рвёт сокет молча. Возвращаемся — проверяем,
  // не остался ли висеть ответ.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && busyRef.current) {
        retryAttempt.current = 0
        scheduleResume()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [scheduleResume])

  // Смена диалога — подгружаем его историю.
  useEffect(() => {
    if (convId === null) {
      setMessages([])
      return
    }
    // Кроме случая, когда в этот диалог прямо сейчас пишут: сообщения хода
    // живут только здесь, на сервере их ещё нет, и «история» затёрла бы их.
    if (streamingConv.current === convId) return
    let cancelled = false
    api
      .messages(convId)
      .then((rows) => {
        if (cancelled) return
        setMessages(
          rows.map((r) => ({ id: nextId(), role: r.role, text: r.content })),
        )
      })
      .catch(() => {
        if (!cancelled) setMessages([])
      })
    return () => {
      cancelled = true
    }
  }, [convId])

  const send = useCallback(
    async (text: string, attachments: UploadedFile[] = []) => {
      const trimmed = text.trim()
      if (!trimmed && attachments.length === 0) return
      if (!transport.current) throw new Error('Соединение ещё устанавливается. Попробуйте отправить ещё раз.')
      if (busyRef.current) throw new Error('Дождитесь ответа или остановите его.')

      // Файлы уже лежат в песочнице: сервер сам покажет картинку модели
      // кадром, а остальные перечислит путями для fs-тула.
      retryAttempt.current = 0

      let target = convId
      if (target === null) {
        // Claim the conversation before announcing it: switching to it fires
        // the history effect below, and the history of a conversation created
        // one millisecond ago is empty — it used to land after the optimistic
        // message and wipe the whole turn off the screen.
        target = (await ensureConversation?.()) ?? null
        streamingConv.current = target
        if (target !== null) onConvCreated(target)
      } else {
        streamingConv.current = target
      }

      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'user',
          text: trimmed || attachments.map((f) => f.name).join(', '),
          files: attachments.map((f) => ({ path: f.path, name: f.name })),
        },
        { id: nextId(), role: 'assistant', text: '', streaming: true },
      ])
      setBusyBoth(true)

      try {
        await transport.current.send({
          convId: target,
          text: trimmed,
          clientId: crypto.randomUUID(),
          attachments: attachments.map((f) => ({ path: f.path, name: f.name })),
          onEvent: handleEvent,
        })
      } catch (e) {
        handleEvent({ type: 'error', message: String(e) })
      }
    },
    [convId, ensureConversation, handleEvent, onConvCreated, setBusyBoth],
  )

  const stop = useCallback(() => {
    if (retryTimer.current) clearTimeout(retryTimer.current)
    retryAttempt.current = RETRY_DELAYS_MS.length // больше не переподключаемся
    // Просим сервер остановиться отдельным запросом: у SSE нет обратного
    // канала, а обрыв соединения генерацию больше не снимает.
    void api.cancel(streamingConv.current ?? convId).catch(() => {})
    transport.current?.cancel()
    patchLast((m) => ({ ...m, streaming: false, text: m.text || 'остановлено' }))
    setBusyBoth(false)
    setReconnecting(false)
    streamingConv.current = null
  }, [convId, patchLast, setBusyBoth])

  return {
    messages,
    busy,
    reconnecting,
    transportKind,
    send,
    stop,
  }
}
